import { ProjectElementNodeWithDrawingDesignation } from 'api/graphQL/types/projectElement.types'

/**
 * Error types for Product Type ID resolution
 */
export type ProductTypeIdResolutionError =
    | 'PT_NOT_FOUND'
    | 'CONTENT_NODE_NOT_FOUND'
    | 'MISSING_DTV_ID'

/**
 * Selected project element node structure
 */
export interface SelectedProjectElementNode {
    elementType?: string | null
    elementId?: string | null
    [key: string]: unknown
}

/**
 * Content item view node structure with elements
 */
export interface ContentItemViewNode {
    id?: string
    dtvItemId?: string
    elements?: Array<{ id?: string }>
    [key: string]: unknown
}

/**
 * Parameters for resolving Product Type IDs
 */
export interface ResolveProductTypeIdsParams {
    /** Currently selected project element node (Family or PT) */
    selectedProjectElementNode: SelectedProjectElementNode | null | undefined
    /** All content item view nodes */
    contentItemViewNodes: ContentItemViewNode[]
    /** Content item ID being edited */
    contentItemId: string
    /** Product types added to this family (with drawing designations) */
    familyAddedProductTypes: Partial<ProjectElementNodeWithDrawingDesignation>[]
    /** Parent family ID (used when PT is selected directly) */
    parentFamilyId?: string
}

/**
 * Result of Product Type ID resolution
 */
export interface ResolvedProductTypeIds {
    /** Project-specific Product Type element ID */
    productTypeElementId: string
    /** Product Family element ID */
    productFamilyId: string
    /** Error type if resolution failed */
    error?: ProductTypeIdResolutionError
}

/**
 * Resolves Product Type and Family IDs for drawing designation save operation.
 *
 * Handles two scenarios:
 * 1. **Direct PT Selection**: Uses PT's elementId directly and parentFamilyId
 * 2. **Family Selection**: Maps content node's dtvItemId (baseElementId) to PT's project elementId
 *
 * @param params - Parameters for ID resolution
 * @returns Resolved IDs or error information
 */
export const resolveProductTypeIds = ({
    selectedProjectElementNode,
    contentItemViewNodes,
    contentItemId,
    familyAddedProductTypes,
    parentFamilyId,
}: ResolveProductTypeIdsParams): ResolvedProductTypeIds => {
    const isFamilySelected =
        selectedProjectElementNode?.elementType === 'PRODUCTFAMILY'

    // Direct PT selection: simple case
    // Use PT's elementId and parent family ID directly
    if (!isFamilySelected) {
        return {
            productTypeElementId: selectedProjectElementNode?.elementId || '',
            productFamilyId: parentFamilyId || '',
        }
    }

    // Family selection: resolve PT by mapping baseElementId
    // Step 1: Find content node containing this contentItemId
    const nodeWithContent = contentItemViewNodes.find((n) =>
        n.elements?.some((el) => el.id === contentItemId)
    )

    if (!nodeWithContent) {
        return {
            productTypeElementId: '',
            productFamilyId: '',
            error: 'CONTENT_NODE_NOT_FOUND',
        }
    }

    if (!nodeWithContent.dtvItemId) {
        return {
            productTypeElementId: '',
            productFamilyId: '',
            error: 'MISSING_DTV_ID',
        }
    }

    // Step 2: Match node's dtvItemId (baseElementId) with familyAddedProductTypes
    const matchedPT = familyAddedProductTypes.find(
        (pt) => pt.baseElementId === nodeWithContent.dtvItemId
    )

    if (!matchedPT?.elementId) {
        return {
            productTypeElementId: '',
            productFamilyId: '',
            error: 'PT_NOT_FOUND',
        }
    }

    // Step 3: Return matched PT's elementId and Family's elementId as productFamilyId
    return {
        productTypeElementId: matchedPT.elementId,
        productFamilyId: selectedProjectElementNode.elementId || '',
    }
}

/**
 * Human-readable error messages for Product Type ID resolution errors
 */
export const PRODUCT_TYPE_ID_ERROR_MESSAGES: Record<
    ProductTypeIdResolutionError,
    string
> = {
    PT_NOT_FOUND: 'Unable to identify Product Type for saving',
    CONTENT_NODE_NOT_FOUND: 'Content node not found',
    MISSING_DTV_ID: 'Missing Product Type identifier',
}
